import React, { useCallback, useState, useEffect, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { connect } from 'react-redux'

import { lowestRatePricePerCabinTypeMap } from 'api/mappers/cruiseMap'
import CabinFilterTile from './CruiseCabinFilterTile'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import CruiseErrorRenderer from 'components/Cruises/CruiseErrorRenderer'
import CabinOfferTile from './CruiseCabinOfferTile'
import CruiseSelectionModalLoading from './CruiseSelectionModalLoading'
import { sortBy, uniqueBy } from 'lib/arrayUtils'

const ModalStyled = styled(ModalBase)`
  > header > :first-child {
    display: none;
  }

  ${mediaQueryUp.desktop} {
    > header > :first-child {
      display: block;
    }
  }
`

const FilterContainer = styled.div`
  display: flex;
  gap: ${rem(12)};
  align-items: center;
`

const getLowestRatePriceByCabinCode = (
  cabinRates: Array<App.CruiseCabinRateView>,
  code: string,
): number => {
  return cabinRates.filter(
    (rate) => rate.pricedCategoryCode === code)?.sort(
    (a, b) => a.ratePrices.price - b.ratePrices.price)[0]?.ratePrices.price
}

interface Props {
  isOpen: boolean
  sessionId: string
  departureId: string
  offer: App.CruiseOffer
  travelersInfo: Array<App.Occupants>
  cabinRatesError: boolean
  cabinRatesErrorToRender: string
  cabinRatesLoading: boolean
  cabinView: Array<App.CruiseCabinView>
  cabinRates: Array<App.CruiseCabinRateView>
  onClose: () => void;
  onBack: () => void;
}

function CruiseCabinSelectionModal(props: Props) {
  const {
    onBack,
    onClose,
    isOpen,
    cabinView,
    cabinRates,
    departureId,
    cabinRatesError,
    cabinRatesLoading,
    travelersInfo,
    cabinRatesErrorToRender,
    offer,
  } = props

  const [categorySelected, setCategorySelected] = useState<string>(null)

  const onClick = useCallback((categoryTypeName: string) => setCategorySelected(categoryTypeName), [])

  const [
    cabinAvailables,
    filters,
  ] = useMemo(() => {
    if (cabinRates?.length && cabinView?.length) {
      const cabins = cabinView.filter((category) => cabinRates
        .find((rate) => category.cabin.code === rate.pricedCategoryCode))

      const ratesAvailables = cabinRates.filter((rates) => cabinView
        .find(({ cabin }) => rates.pricedCategoryCode === cabin.code))
      // WORKAROUND TO REMOVE DUPLICATE CABINS
      // WHILE WE INVESTIGATE AND SOLVE THE REAL PROBLEM
      return [
        uniqueBy(cabins, cabin => cabin.cabin.code),
        lowestRatePricePerCabinTypeMap(ratesAvailables),
      ]
    }
    return [[], []]
  }, [cabinRates, cabinView])

  const cabinList = useMemo(() => {
    if (categorySelected && cabinAvailables?.length && cabinRates?.length) {
      const filtered = cabinAvailables.filter(({ cabin }) => cabin.typeName === categorySelected)
      return sortBy(filtered, cabinView => getLowestRatePriceByCabinCode(cabinRates, cabinView.cabin.code), 'asc')
    }
    return []
  }, [cabinAvailables, cabinRates, categorySelected])

  useEffect(() => {
    if (cabinRates?.length && cabinView?.length) {
      if (!filters.find(item => item.typeName === categorySelected)) {
        setCategorySelected(filters[0]?.typeName)
      }
    }
  }, [cabinRates, cabinView, categorySelected, filters])

  return (
    <ModalStyled
      size="large"
      isOpen={isOpen}
      onClose={onClose} >

      <ModalHeader
        onBackButtonClick={onBack}
        onCloseButtonClick={onClose}
        title={cabinRatesLoading ? 'Checking availability...' : 'What cabin type would you like?'}
      />

      <ModalBody>
        <ModalContent>
          <CruiseSelectionModalLoading
            cruiseLine={offer?.cruiseLine}
            loading={cabinRatesLoading}
          />

          <>
            {!cabinRatesLoading && !cabinRatesError && (
              <FilterContainer>
                {filters.map((cabin, i) => (
                  <CabinFilterTile
                    onClick={onClick}
                    key={cabin.typeName + i}
                    price={cabin.lowestPrice}
                    cabinType={cabin.typeName}
                    selected={categorySelected === cabin.typeName}
                    disabled={!cabinRates?.some((rate) => rate.cabinType === cabin.typeName)} />
                ))}
              </FilterContainer>
            )}

            {!cabinRatesLoading && cabinRatesError && (
              <CruiseErrorRenderer textToRender={cabinRatesErrorToRender} />
            )}

            {(!cabinRatesLoading && !cabinRatesError && cabinList?.length) && (
              cabinList.map((item, i) => (
                <CabinOfferTile
                  {...props}
                  travelersInfo={travelersInfo}
                  id={departureId}
                  cabin={item.cabin}
                  key={`${item.id}${departureId}${i}`} />
              ))
            )}
          </>
        </ModalContent>
      </ModalBody>
    </ModalStyled>
  )
}

const mapStateToProps = (appState: App.State) => {
  return {
    cabinRates: appState.cruise.cabinRates,
    cabinRatesError: appState.cruise.cabinRatesError,
    cabinRatesErrorToRender: appState.cruise.cabinRatesErrorToRender,
    cabinRatesLoading: appState.cruise.cabinRatesLoading,
    sessionId: appState.cruise.revelexSessionId,
  }
}

export default connect(mapStateToProps)(CruiseCabinSelectionModal)
